import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface DataPointMeta {
  fieldName: string;
  sourceLabel: string | null;
  sourceUrl: string | null;
  confidence: string;
  isEstimated: boolean;
  lastVerifiedAt: string | null;
}

export type DataPointMap = Map<string, DataPointMeta>;

/**
 * Henter registrerede datapunkter for en variant (og evt. dens model).
 * Et felt betragtes som UBEKRÆFTET når der findes et datapunkt uden verifikationsdato.
 * Felter uden datapunkt får ingen badge — vi påstår intet om dem.
 */
export function useDataPointMeta(entityIds: (string | null | undefined)[]): DataPointMap {
  const [map, setMap] = useState<DataPointMap>(new Map());
  const ids = entityIds.filter(Boolean) as string[];
  const key = ids.slice().sort().join(',');

  useEffect(() => {
    if (!key) {
      setMap(new Map());
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        const { data, error } = await supabase
          .from('data_points')
          .select('field_name, source_label, source_url, confidence, is_estimated, last_verified_at')
          .in('entity_id', key.split(','))
          .limit(400);

        if (error || cancelled || !data) return;

        const next: DataPointMap = new Map();
        for (const row of data) {
          const existing = next.get(row.field_name);
          // Behold det nyest verificerede datapunkt pr. felt.
          if (existing && existing.lastVerifiedAt && !row.last_verified_at) continue;
          next.set(row.field_name, {
            fieldName: row.field_name,
            sourceLabel: row.source_label,
            sourceUrl: row.source_url,
            confidence: row.confidence,
            isEstimated: row.is_estimated,
            lastVerifiedAt: row.last_verified_at,
          });
        }
        setMap(next);
      } catch {
        // Stille fejl — siden fungerer uden datapunkt-metadata
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [key]);

  return map;
}

const DataPointContext = createContext<DataPointMap>(new Map());

export function DataPointProvider({ value, children }: { value: DataPointMap; children: ReactNode }) {
  return <DataPointContext.Provider value={value}>{children}</DataPointContext.Provider>;
}

export function useFieldMeta(field?: string): DataPointMeta | null {
  const map = useContext(DataPointContext);
  if (!field) return null;
  return map.get(field) ?? null;
}

/** Sandt når feltet er registreret, men endnu ikke verificeret af redaktionen. */
export function useIsUnverified(field?: string): boolean {
  const meta = useFieldMeta(field);
  return !!meta && !meta.lastVerifiedAt;
}
